import React, { useEffect, useState } from 'react';
import Button from '../../../components/bootstrap/Button';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import Input from '../../../components/bootstrap/forms/Input';
import Select from '../../../components/bootstrap/forms/Select';
import { searchPythonAPI } from '../../../apis/backend/apiPython';
import { formatCurrency } from '../../comptabilite/utils/functions';
import { getTaxInfo } from 'common/function/functionsUtils';

const DevisTotauxConfig = ({
	devis,
	company,
	onUpdateRemise,
	onUpdateTVA,
	loading = false,
	lignes = [],
}) => {
	const [taxs, setTaxs] = useState([]);
	const [defaultTaxs, setDefaultTax] = useState([]);
	const [loadingTaxes, setLoadingTaxes] = useState(false);
	// Récupérer les taxes depuis l'API Python
	// Récupérer les taxes depuis l'API Python
	const handleGetTaxes = () => {
		// 1. Safety check
		if (!company?._id) return;

		setLoadingTaxes(true);
		try {
			const country = company?.address?.country?.toLowerCase() || 'france';

			// 1. Get data from helper
			const { taxes, defaultTax } = getTaxInfo(country);

			// Update local states
			setDefaultTax(defaultTax);
			setTaxs(taxes);

			const tvaIsMissing = isNaN(parseFloat(devis.tva))
			const tvaNotAllowed = !tvaIsMissing && !taxes.includes(devis.tva);

			if (tvaIsMissing || tvaNotAllowed) {
				console.log(tvaNotAllowed ? "⚠️ Current TVA not in list, resetting to default" : "ℹ️ Auto-selecting default TVA");

				if (defaultTax !== undefined) {
					onUpdateTVA({
						tva: defaultTax,
					});
				}
			}
		} catch (error) {
			console.error('Erreur lors de la récupération des taxes:', error);
		} finally {
			setLoadingTaxes(false);
		}
	};
	useEffect(() => {
		if (company?._id) {
			handleGetTaxes();
		}
	}, [company?._id]);

	useEffect(() => {
		if (!devis.remiseType) {
			onUpdateRemise({
				remiseType: 'Aucune',
				remiseValue: 0,
			});
		}

		if (isNaN(parseFloat(devis.tva))) {
			// This triggers if devis.tva is null, undefined, "", or "abc"
			// But it will NOT trigger if devis.tva is 0 or "20"
			onUpdateTVA({ tva: defaultTaxs });
		}
	}, [devis.remiseType, devis.tva, defaultTaxs]);

	const handleRemiseTypeChange = (value) => {
		onUpdateRemise({
			remiseType: value,
			remiseValue: value === 'Aucune' ? 0 : devis.remiseValue || 0,
		});
	};

	const handleRemiseValueChange = (value) => {
		const numValue = parseFloat(value) || 0;

		// Validation frontend
		if (devis.remiseType === 'Pourcentage' && numValue > 100) {
			alert('Le pourcentage de remise ne peut pas dépasser 100%');
			return;
		}

		if (numValue < 0) {
			alert('La valeur de remise ne peut pas être négative');
			return;
		}

		onUpdateRemise({
			remiseType: devis.remiseType || 'Aucune',
			remiseValue: numValue,
		});
	};

	const handleTVAChange = (selectedTax) => {
		if (selectedTax) {
			try {
				onUpdateTVA({
					tva: selectedTax,
				});
			} catch (error) {
				console.error('Erreur lors du parsing de la taxe:', error);
			}
		}
	};

	// Calcul des totaux basé sur les lignes
	const calculateTotaux = () => {
		// Total HT des lignes (uniquement les lignes avec prix)
		const totalHT = lignes.reduce((total, ligne) => {
			if (ligne.type === 'note') return total; // Les notes n'ont pas de valeur monétaire

			const quantite = parseFloat(ligne.quantite) || 0;
			const prixUnitaireHT = parseFloat(ligne.prixUnitaireHT) || 0;
			return total + prixUnitaireHT * quantite;
		}, 0);

		// Calcul de la remise globale
		let montantRemise = 0;
		const remiseType = devis.remiseType || 'Aucune';
		const remiseValue = parseFloat(devis.remiseValue) || 0;

		if (remiseType === 'Pourcentage' && remiseValue > 0) {
			montantRemise = totalHT * (remiseValue / 100);
		} else if (remiseType === 'Montant' && remiseValue > 0) {
			montantRemise = Math.min(remiseValue, totalHT);
		}

		// HT après remise
		const totalHTApresRemise = Math.max(0, totalHT - montantRemise);

		// Calcul TVA globale
		const tva = devis.tva || 0; // 0% par défaut
		const montantTVA = totalHTApresRemise * (tva / 100);
		const totalTTC = totalHTApresRemise + montantTVA;

		return {
			totalHT,
			montantRemise,
			totalHTApresRemise,
			montantTVA,
			totalTTC,
		};
	};

	const totaux = calculateTotaux();

	// Obtenir la valeur actuelle pour le select TVA
	const getCurrentTaxValue = () => {
		if ((devis.tva === null || devis.tva === undefined)) {
			return 0;
		}
		try {
			return devis.tva;
		} catch (error) {
			console.error('Erreur lors de la sérialisation de la taxe:', error);
			return '';
		}
	};

	// Vérifier si une taxe 0% existe

	return (
		<Card className='mt-4 border border-gray-300 bg-white'>
			<CardBody>
				<h5 className='mb-4 text-dark'>Configuration des Totaux</h5>

				<div className='row g-3'>
					{/* Configuration Remise */}
					<div className='col-md-6'>
						<label className='form-label text-dark'>Remise Globale</label>
						<div className='row g-2'>
							<div className='col-6'>
								<Select
									value={devis.remiseType || 'Aucune'}
									onChange={(e) => handleRemiseTypeChange(e.target.value)}
									disabled={loading}
									size='sm'>
									<option value='Aucune'>Aucune remise</option>
									<option value='Pourcentage'>Pourcentage %</option>
									<option value='Montant'>Montant {company?.currency}</option>
								</Select>
							</div>
							<div className='col-6'>
								{devis.remiseType !== 'Aucune' && (
									<Input
										type='number'
										step={devis.remiseType === 'Pourcentage' ? '0.1' : '0.01'}
										min='0'
										max={devis.remiseType === 'Pourcentage' ? '100' : undefined}
										value={devis.remiseValue || 0}
										onChange={(e) => handleRemiseValueChange(e.target.value)}
										disabled={loading}
										size='sm'
										placeholder={
											devis.remiseType === 'Pourcentage' ? '0.0' : '0.00'
										}
									/>
								)}
							</div>
						</div>
						{devis.remiseType !== 'Aucune' && (
							<small className='text-muted'>
								{devis.remiseType === 'Pourcentage'
									? `${devis.remiseValue || 0}% de remise`
									: `${devis.remiseValue || 0}€ de remise`}
							</small>
						)}
					</div>

					{/* Configuration TVA */}
					<div className='col-md-6'>
						<label className='form-label text-dark'>
							TVA{' '}
							{loadingTaxes && <small className='text-muted'>(Chargement...)</small>}
						</label>
						<Select
							value={getCurrentTaxValue()}
							onChange={(e) => handleTVAChange(e.target.value)}
							disabled={loading || loadingTaxes || taxs.length === 0}
							size='sm'>
							<option value=''>Sélectionner une TVA</option>
							{taxs.map((tax) => (
								<option key={tax} value={tax}>
									{tax}%
								</option>
							))}
						</Select>

						{/* Indicateur de taxe par défaut */}

						{/* Affichage du taux actuel */}
						<small className='text-muted d-block mt-1'>
							Taux actuel: <strong>{devis.tva || 0}%</strong>
						</small>
					</div>
				</div>

				{/* Récapitulatif des totaux */}
				<div className='mt-4 p-3 border border-gray-300 rounded bg-light'>
					<h6 className='text-dark mb-3'>Récapitulatif du Devis</h6>
					<div className='row'>
						<div className='col-md-4 mb-2'>
							<div className='d-flex justify-content-between'>
								<span className='text-muted'>Total HT:</span>
								<strong className='text-dark'>
									{formatCurrency(totaux.totalHT, company?.currency)}
								</strong>
							</div>
						</div>

						{totaux.montantRemise > 0 && (
							<>
								<div className='col-md-4 mb-2'>
									<div className='d-flex justify-content-between'>
										<span className='text-muted'>Remise:</span>
										<strong className='text-danger'>
											-
											{formatCurrency(
												totaux.montantRemise,
												company?.currency,
											)}
										</strong>
									</div>
								</div>
								<div className='col-md-4 mb-2'>
									<div className='d-flex justify-content-between'>
										<span className='text-muted'>HT après remise:</span>
										<strong className='text-dark'>
											{formatCurrency(
												totaux.totalHTApresRemise,
												company?.currency,
											)}
										</strong>
									</div>
								</div>
							</>
						)}

						<div className='col-md-4 mb-2'>
							<div className='d-flex justify-content-between'>
								<span className='text-muted'>TVA ({devis.tva || 0}%):</span>
								<strong className='text-dark'>
									{formatCurrency(totaux.montantTVA, company?.currency)}
								</strong>
							</div>
						</div>

						<div className='col-md-4 mb-2'>
							<div className='d-flex justify-content-between'>
								<span className='text-muted fw-bold'>Total TTC:</span>
								<strong className='text-success fs-5'>
									{formatCurrency(totaux.totalTTC, company?.currency)}
								</strong>
							</div>
						</div>
					</div>

					{/* Détails de la remise appliquée */}
					{devis.remiseType !== 'Aucune' && devis.remiseValue > 0 && (
						<div className='mt-3 pt-2 border-top'>
							<small className='text-muted'>
								Remise appliquée:{' '}
								<strong>
									{devis.remiseType === 'Pourcentage'
										? `${devis.remiseValue}%`
										: `${devis.remiseValue}`}
								</strong>
							</small>
						</div>
					)}
				</div>
			</CardBody>
		</Card>
	);
};

export default DevisTotauxConfig;
